/**
 * Results card — Cephalic Index + Cranial Vault Asymmetry Index, with their
 * classification labels and a variant-paragraph clinical summary. Includes
 * the Download PDF button (the soft-gate trigger).
 *
 * The section is ALWAYS visible — keeping the right column rendered prevents
 * an awkward layout shift when results land. Pre-Calculate, an empty-state
 * styling (`.results--empty`) dims the values, swaps in em-dash placeholders,
 * surfaces a "tap Calculate" prompt in the variant paragraph slot, and
 * disables the Download button. Post-Calculate, the class is removed and
 * real values fill in.
 *
 * Variant-paragraph rendering: the source markdown holds template strings
 * with [Baby's Name] and [him/her/them] placeholders. The PDF substitutes
 * the entered baby name (or "Your baby" fallback); the screen does the same
 * substitution. Pronoun is hardcoded to "them" everywhere — the DC doesn't
 * pick a pronoun in the form.
 */

import { CLINICAL_CONTENT } from "../generated/clinical-content";
import { el } from "../dom";
import type { CIClass, CVAIClass, SummaryVariant } from "../calc";
import type { Action, AppState } from "../state";

import "./results.css";

export interface ResultsDeps {
  dispatch: (action: Action) => void;
}

export interface ResultsHandle {
  el: HTMLElement;
  update: (state: AppState) => void;
}

/**
 * Convert a variant template to display prose. If the DC entered a baby name
 * in the form, substitute it; otherwise fall back to "Your baby". Pronoun
 * placeholder always becomes "them" (we don't ask the DC for pronoun).
 */
function humanizeVariant(template: string, babyName: string): string {
  const trimmed = babyName.trim();
  const name = trimmed === "" ? "Your baby" : trimmed;
  return template.replace(/\[Baby's Name\]/g, name).replace(/\[him\/her\/them\]/g, "them");
}

function variantText(variant: SummaryVariant, babyName: string): string {
  return humanizeVariant(CLINICAL_CONTENT.summaryVariants[variant], babyName);
}

function ciLabel(cls: CIClass): string {
  return CLINICAL_CONTENT.ciLabels[cls];
}

function cvaiLabel(cls: CVAIClass): string {
  return CLINICAL_CONTENT.cvaiLabels[cls];
}

export function createResults(deps: ResultsDeps): ResultsHandle {
  // ---- Cephalic Index card ----
  const ciValueEl = el("div", { class: "results__value" });
  const ciClassEl = el("div", { class: "results__classification" });
  const ciCard = el(
    "div",
    { class: "results__card" },
    el("div", { class: "results__card-label" }, "Cephalic Index"),
    ciValueEl,
    ciClassEl,
    el("p", { class: "results__card-formula" }, "(parietal width ÷ AP length) × 100"),
  );

  // ---- CVAI card ----
  const cvaiValueEl = el("div", { class: "results__value" });
  const cvaiClassEl = el("div", { class: "results__classification" });
  const cvaiCard = el(
    "div",
    { class: "results__card" },
    el("div", { class: "results__card-label" }, "Cranial Vault Asymmetry Index"),
    cvaiValueEl,
    cvaiClassEl,
    el("p", { class: "results__card-formula" }, "|R − L| ÷ longer diagonal × 100"),
  );

  // ---- Variant paragraph ----
  const variantEl = el("p", { class: "results__variant-paragraph" });

  // ---- Download button + error region ----
  const downloadBtn = el(
    "button",
    {
      type: "button",
      class: "results__download",
      onclick: () => deps.dispatch({ type: "download-requested" }),
    },
    "Download parent handout (PDF)",
  );

  const errorBox = el(
    "div",
    { class: "results__error", role: "alert", "aria-live": "polite", hidden: true },
    el("p", { class: "results__error-message" }),
    el(
      "button",
      {
        type: "button",
        class: "results__error-dismiss",
        onclick: () => deps.dispatch({ type: "pdf-error-dismissed" }),
      },
      "Dismiss",
    ),
  );
  const errorMessageEl = errorBox.querySelector(".results__error-message") as HTMLElement;

  const root = el(
    "section",
    {
      class: "results results--empty",
      "aria-labelledby": "results-heading",
    },
    el("h2", { class: "results__heading", id: "results-heading" }, "Results"),
    el("div", { class: "results__cards" }, ciCard, cvaiCard),
    variantEl,
    downloadBtn,
    errorBox,
  );

  function update(state: AppState): void {
    const flow = state.flow;
    const hasResults =
      flow.tag === "results" || flow.tag === "generating-pdf" || flow.tag === "pdf-error";

    if (!hasResults) {
      // Empty state — keep the column visible (no layout shift when calc
      // lands) but show dimmed em-dash placeholders + a friendly prompt.
      // The .results--empty class drives the dim styling in results.css.
      root.classList.add("results--empty");
      ciValueEl.textContent = "—";
      ciClassEl.textContent = "—";
      cvaiValueEl.textContent = "—";
      cvaiClassEl.textContent = "—";
      variantEl.textContent = "Enter measurements and tap Calculate to see your baby's results.";
      downloadBtn.disabled = true;
      downloadBtn.textContent = "Download parent handout (PDF)";
      errorBox.hidden = true;
      errorMessageEl.textContent = "";
      return;
    }
    root.classList.remove("results--empty");

    const r = flow.results;
    ciValueEl.textContent = r.ci.toFixed(1);
    ciClassEl.textContent = ciLabel(r.ciClass);
    cvaiValueEl.textContent = r.cvai.toFixed(1);
    cvaiClassEl.textContent = cvaiLabel(r.cvaiClass);
    variantEl.textContent = variantText(r.variant, flow.values.babyName);

    // Download button state — generating shows a busy label, error keeps the
    // button visible but the error box appears below it.
    if (flow.tag === "generating-pdf") {
      downloadBtn.disabled = true;
      downloadBtn.textContent = "Generating PDF…";
    } else {
      downloadBtn.disabled = false;
      downloadBtn.textContent = "Download parent handout (PDF)";
    }

    if (flow.tag === "pdf-error") {
      errorBox.hidden = false;
      errorMessageEl.textContent = `Couldn't generate the PDF: ${flow.message}`;
    } else {
      errorBox.hidden = true;
      errorMessageEl.textContent = "";
    }
  }

  return { el: root, update };
}
